import { Event, SDK, ServerOptions } from '@commercetools/frontend-sdk';
import { GetProductQuery, ProductQueryQuery, QueryProductCategoriesQuery } from '../../types/queries/ProductQueries';
import {
  GetAvailabilityMessageAction,
  GetProductAction,
  GetRecentlyViewedProductAction,
  GetProductSuppliesAction,
  GetSearchableProductAttributesAction,
  GetShopCollectionAction,
  ProductQueryAction,
  QueryProductCategoriesAction,
  ShareProductLink,
} from '../../types/actions/ProductActions';
import { Product, FilterField, Category, Inventory } from 'shared/types/product';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import { PaginatedResult, ProductPaginatedResult } from 'shared/types/result';
import { ProductCardProduct } from 'shared/types/product/Product';
import { ShareProductLinkPayload } from '../../types/payloads/ProductPayloads';

export type ProductActions = {
  getProduct: GetProductAction;
  query: ProductQueryAction;
  queryCategories: QueryProductCategoriesAction;
  getSearchableAttributes: GetSearchableProductAttributesAction;
  getRecentlyViewedProduct: GetRecentlyViewedProductAction;
  getAvailabilityMessage: GetAvailabilityMessageAction;
  getShopCollection: GetShopCollectionAction;
  getProductSupplies: GetProductSuppliesAction;
  shareProductLink: ShareProductLink;
};

export const getProductActions = (sdk: SDK<ComposableCommerceEvents>): ProductActions => {
  return {
    getProduct: async (
      query: GetProductQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<Product>({
        actionName: 'product/getProduct',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError && response.data) {
        sdk.trigger(
          new Event({
            eventName: 'productFetched',
            data: {
              product: response.data,
            },
          }),
        );
      }
      return response;
    },
    query: async (
      query: ProductQueryQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<ProductPaginatedResult>({
        actionName: 'product/query',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productsQueried',
            data: {
              query: query,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    queryCategories: async (
      query: QueryProductCategoriesQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<PaginatedResult<Category>>({
        actionName: 'product/queryCategories',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productCategoriesQueried',
            data: {
              query: query,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    getSearchableAttributes: async (
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<FilterField[]>({
        actionName: 'product/searchableAttributes',
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'searchableProductAttributesFetched',
            data: {
              filterFields: response.data,
            },
          }),
        );
      }
      return response;
    },
    getProductSupplies: async (
      supplies: string[],
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ProductCardProduct[]>({
        actionName: 'product/getProductSupplies',
        parallel: options.parallel,
        query: { supplies },
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productSuppliesQueried',
            data: {
              supplies,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    getRecentlyViewedProduct: async (
      sku: string,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ProductCardProduct>({
        actionName: 'product/getRecentViewedProduct',
        parallel: options.parallel,
        query: { sku },
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'recentlyViewedProductQueried',
            data: {
              sku,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },

    getAvailabilityMessage: async (
      sku: string,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<Inventory>({
        actionName: 'product/getAvailabilityMessage',
        parallel: options.parallel,
        query: { sku },
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'availabilityMessageQueried',
            data: {
              sku,
              result: response.data,
            },
          }),
        );
      }

      return response;
    },

    shareProductLink: async (
      payload: ShareProductLinkPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction({
        actionName: 'product/shareProductLink',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      return response;
    },
    getShopCollection: async (
      query: ProductQueryQuery,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ProductPaginatedResult>({
        actionName: 'product/getShopCollection',
        parallel: options.parallel,
        query,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'shopCollectionQueried',
            data: {
              query: { ...query },
              result: response.data,
            },
          }),
        );
      }

      return response;
    },
  };
};
